/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {AppContext} from "../../../misc/appContext";
import {importVats} from "../../../../util/requestSender";
import {ErrorType, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import {ActivateHandle} from "../../../../util/interfaces";
import {createErrorMessage, downloadFileURL} from "../../../../util/utils";
import ImportVat from "./importVat";

interface Props {
    onSuccess: VoidFunction;
}

function ImportVatController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {onSuccess} = props;
    const [importFile, setImportFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(false);
    const [error, setError] = useState<ErrorType>(null);
    const [applyRetroactive, setApplyRetroactive] = useState(false);
    const errorFile = useRef<string | null>(null);

    function onDownloadErrorFile() {
        if (!errorFile.current) return;

        const link = document.createElement('a');
        link.href = errorFile.current;
        link.download = 'import-fouten.xlsx';
        link.click();
    }

    function onActivate() {
        setActive(true);
    }

    function onClose() {
        setActive(false);
        setImportFile(null);
        setLoading(false);
        setError(null);
        if (errorFile.current)
            URL.revokeObjectURL(errorFile.current);
        errorFile.current = null;
    }

    function onChangeApplyRetroactive() {
        setApplyRetroactive(!applyRetroactive);
    }

    function onDownloadTemplate() {
        const fileURL = 'https://app.bolmate.nl/static/btw-import-template-v2.xlsx';
        downloadFileURL(fileURL, 'bolmate-btw-import-template.xlsx');
    }

    function onChangeFile(evt: React.ChangeEvent<HTMLInputElement>) {
        const {files} = evt.target;
        if (!files) return;
        setImportFile(files[0]);
    }

    function onSubmit() {
        const {account} = context.accountContext;
        if (!account || !importFile) return;
        if (!importFile.name.toLowerCase().endsWith('.xlsx'))
            return showDialog('We accepteren alleen .xlsx bestanden, gebruik het voorbeeldbestand.')
        setLoading(true);
        importVats({
            file: importFile, account_id: account.id, create_non_existing: false,
            apply_retroactive: applyRetroactive,
            callback: (data: ResponseData, response: Response) => {
                setLoading(false);
                if (response.headers.get('Content-Type') === 'application/json') {
                    if (data.success) {
                        const {num_imported} = data;
                        const countLabel = num_imported === 1 ? 'regel' : 'regels';
                        onSuccess();
                        onClose();
                        showSnackbar(`Succesvol ${num_imported} ${countLabel} bijgewerkt`);
                    } else
                        showDialog(createErrorMessage(data.message ??
                            'Het importeren van de BTW percentages is niet gelukt.'), 'Importeren mislukt');
                } else if (data.status === 'success') {
                    response.blob().then((blob: Blob) => {
                        const numError = Number(response.headers.get('X-Num-Error') ?? 0);
                        const numImported = Number(response.headers.get('X-Num-Imported') ?? 0);
                        // Revoke old URL before creating a new one
                        if (errorFile.current) URL.revokeObjectURL(errorFile.current);
                        errorFile.current = URL.createObjectURL(blob);
                        setError(
                            `${numImported} regels zijn geïmporteerd, maar ${numError} kon${numError === 1 ? '' : 'den'} niet
                        geïmporteerd worden. Zie het bestand hieronder voor de details.`)
                    });
                }
            }
        });
    }

    useEffect(() => {
        return () => {
            if (errorFile.current) URL.revokeObjectURL(errorFile.current);
        };
    }, []);

    useImperativeHandle(ref, () => ({
        activate: onActivate
    }));

    return (
        <ImportVat onClose={onClose} onDownloadTemplate={onDownloadTemplate} onChangeFile={onChangeFile}
                   onSubmit={onSubmit} onDownloadErrorFile={onDownloadErrorFile}
                   onChangeApplyRetroactive={onChangeApplyRetroactive} importFile={importFile} loading={loading}
                   active={active} error={error} applyRetroactive={applyRetroactive}/>
    );
}

export default forwardRef<ActivateHandle, Props>(ImportVatController);